import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTransacoesFinanceirasFiltradas, type FinanceiroFiltroParams } from './useFinanceiro';
import { useSinistros } from './useSinistros';
import { supabase } from '@/integrations/supabase/client';
import {
  formatPeriodoLabel,
  getPeriodosEfetivosStats,
} from '@/lib/financeiroFiltros';
import { dedupeTransacoesEntradaParaStats } from '@/lib/financeiroRecebiveis';

export interface FinanceiroStats {
  receitaMensal: number;
  pagamentosPendentes: number;
  pagamentosEmAtraso: number;
  pagamentosPagos: number;
  totalClientesPendentes: number;
  totalClientesEmAtraso: number;
  totalPagamentosRealizados: number;
  percentualCrescimento: number;
  despesasPendentes: number;
  despesasPagas: number;
  totalDespesasMes: number;
  orcamentosAprovadosValor: number;
  /** Indica se há filtro de data ou critérios ativos nos cards */
  temFiltroAtivo: boolean;
  periodoVencimentoLabel: string;
  periodoPagamentoLabel: string;
  mostrarCrescimento: boolean;
}

export function useFinanceiroStats(
  filtros?: FinanceiroFiltroParams | null,
  /** Período legado usado apenas na aba Fluxo de caixa */
  periodoFluxo?: { dataInicio: string; dataFim: string } | null
): FinanceiroStats {
  const periodosEfetivos = useMemo(
    () => (periodoFluxo?.dataInicio && periodoFluxo?.dataFim
      ? { vencimento: periodoFluxo, pagamento: periodoFluxo, temFiltroCustomizado: true }
      : getPeriodosEfetivosStats(filtros)),
    [filtros, periodoFluxo]
  );

  const { data: transacoes = [], error: transacoesError } = useQuery({
    queryKey: ['financeiro', 'stats-transacoes', filtros ?? null, periodoFluxo ?? null],
    queryFn: () => fetchTransacoesFinanceirasFiltradas(filtros),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: sinistros = [] } = useSinistros();
  const { data: orcamentosAprovados = [] } = useQuery({
    queryKey: [
      'orcamentos',
      'aprovados-valor',
      periodosEfetivos.vencimento.dataInicio,
      periodosEfetivos.vencimento.dataFim,
      filtros?.vendedorId ?? null,
    ],
    queryFn: async () => {
      let query = supabase
        .from('orcamentos')
        .select('valor_total, valor_seguro, created_at')
        .eq('status', 'aprovado')
        .eq('ativo', true);

      if (filtros?.vendedorId) {
        query = query.eq('vendedor_id', filtros.vendedorId);
      }

      query = query
        .gte('created_at', `${periodosEfetivos.vencimento.dataInicio}T00:00:00`)
        .lte('created_at', `${periodosEfetivos.vencimento.dataFim}T23:59:59`);

      const { data } = await query;
      return (data || []) as { valor_total?: number; valor_seguro?: number }[];
    },
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const stats = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const { vencimento, pagamento, temFiltroCustomizado } = periodosEfetivos;
    const inicioVenc = new Date(`${vencimento.dataInicio}T00:00:00`);
    const fimVenc = new Date(`${vencimento.dataFim}T23:59:59`);
    const inicioPag = new Date(`${pagamento.dataInicio}T00:00:00`);
    const fimPag = new Date(`${pagamento.dataFim}T23:59:59`);

    const inicioMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const fimMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);

    const periodoVencimentoLabel = formatPeriodoLabel(vencimento);
    const periodoPagamentoLabel = formatPeriodoLabel(pagamento);
    const temFiltroAtivo =
      temFiltroCustomizado ||
      !!(filtros?.valorMin != null || filtros?.valorMax != null ||
        (filtros?.formaPagamento && filtros.formaPagamento !== 'todos') ||
        (filtros?.parcelas && filtros.parcelas !== 'todas') ||
        filtros?.estado || filtros?.cidade ||
        (filtros?.status && filtros.status !== 'todos'));

    const inVencPeriodo = (dataVenc: string) => {
      const d = new Date(`${dataVenc}T12:00:00`);
      return d >= inicioVenc && d <= fimVenc;
    };

    const inPagPeriodo = (dataPag?: string | null) => {
      if (!dataPag) return false;
      const d = new Date(`${dataPag}T12:00:00`);
      return d >= inicioPag && d <= fimPag;
    };

    // Evita inflar cards com espelhos do cron / duplicatas pedido+parcela
    const transacoesUnicas = dedupeTransacoesEntradaParaStats(transacoes);
    const orcamentosAprovadosValor = orcamentosAprovados.reduce(
      (s, o) => s + (o.valor_seguro ?? o.valor_total ?? 0),
      0
    );

    // Com escopo de vendedor: nunca misturar sinistros (evita "Pagos" de outros)
    if (filtros?.vendedorId && (transacoesError || transacoesUnicas.length === 0)) {
      return {
        receitaMensal: 0,
        pagamentosPendentes: 0,
        pagamentosEmAtraso: 0,
        pagamentosPagos: 0,
        totalClientesPendentes: 0,
        totalClientesEmAtraso: 0,
        totalPagamentosRealizados: 0,
        percentualCrescimento: 0,
        despesasPendentes: 0,
        despesasPagas: 0,
        totalDespesasMes: 0,
        orcamentosAprovadosValor,
        temFiltroAtivo,
        periodoVencimentoLabel,
        periodoPagamentoLabel,
        mostrarCrescimento: !temFiltroCustomizado,
      };
    }

    if (transacoesError || transacoesUnicas.length === 0) {
      const receitaMensal = sinistros
        .filter((s) => {
          if (s.status === 'finalizado' && s.valor_aprovado && s.created_at) {
            const dataSinistro = new Date(s.created_at);
            return dataSinistro >= inicioVenc && dataSinistro <= fimVenc;
          }
          return false;
        })
        .reduce((total, s) => total + (s.valor_aprovado || 0), 0);

      const receitaMesAnterior = temFiltroCustomizado
        ? 0
        : sinistros
            .filter((s) => {
              if (s.status === 'finalizado' && s.valor_aprovado && s.created_at) {
                const dataSinistro = new Date(s.created_at);
                return dataSinistro >= inicioMesAnterior && dataSinistro <= fimMesAnterior;
              }
              return false;
            })
            .reduce((total, s) => total + (s.valor_aprovado || 0), 0);

      const percentualCrescimento =
        receitaMesAnterior > 0
          ? ((receitaMensal - receitaMesAnterior) / receitaMesAnterior) * 100
          : 0;

      const pagamentosPendentes = sinistros
        .filter((s) => s.status === 'analise' && s.valor_estimado)
        .reduce((total, s) => total + (s.valor_estimado || 0), 0);

      const pagamentosEmAtraso = sinistros
        .filter((s) => s.status === 'aberto' && s.valor_estimado)
        .reduce((total, s) => total + (s.valor_estimado || 0), 0);

      const pagamentosPagos = sinistros
        .filter((s) => s.status === 'finalizado' && s.valor_aprovado)
        .reduce((total, s) => total + (s.valor_aprovado || 0), 0);

      const clientesPendentes = new Set(
        sinistros.filter((s) => s.status === 'analise').map((s) => s.cliente_id)
      );
      const clientesEmAtraso = new Set(
        sinistros.filter((s) => s.status === 'aberto').map((s) => s.cliente_id)
      );

      return {
        receitaMensal,
        pagamentosPendentes,
        pagamentosEmAtraso,
        pagamentosPagos,
        totalClientesPendentes: clientesPendentes.size,
        totalClientesEmAtraso: clientesEmAtraso.size,
        totalPagamentosRealizados: sinistros.filter((s) => s.status === 'finalizado').length,
        percentualCrescimento,
        despesasPendentes: 0,
        despesasPagas: 0,
        totalDespesasMes: 0,
        orcamentosAprovadosValor,
        temFiltroAtivo,
        periodoVencimentoLabel,
        periodoPagamentoLabel,
        mostrarCrescimento: !temFiltroCustomizado,
      };
    }

    const receitaMensal = transacoesUnicas
      .filter(
        (t) =>
          t.tipo === 'entrada' &&
          t.status === 'pago' &&
          inPagPeriodo(t.data_pagamento)
      )
      .reduce((total, t) => total + t.valor, 0);

    const receitaMesAnterior = temFiltroCustomizado
      ? 0
      : transacoesUnicas
          .filter((t) => {
            if (t.tipo !== 'entrada' || t.status !== 'pago') return false;
            const dataRef = t.data_pagamento
              ? new Date(`${t.data_pagamento}T12:00:00`)
              : new Date(`${t.data_vencimento}T12:00:00`);
            return dataRef >= inicioMesAnterior && dataRef <= fimMesAnterior;
          })
          .reduce((total, t) => total + t.valor, 0);

    const percentualCrescimento =
      receitaMesAnterior > 0
        ? ((receitaMensal - receitaMesAnterior) / receitaMesAnterior) * 100
        : 0;

    const pagamentosPendentes = transacoesUnicas
      .filter(
        (t) =>
          t.tipo === 'entrada' &&
          t.status === 'pendente' &&
          inVencPeriodo(t.data_vencimento)
      )
      .reduce((total, t) => total + t.valor, 0);

    const pagamentosEmAtraso = transacoesUnicas
      .filter((t) => {
        const dataVencimento = new Date(`${t.data_vencimento}T00:00:00`);
        return (
          t.tipo === 'entrada' &&
          t.status === 'pendente' &&
          dataVencimento < hoje
        );
      })
      .reduce((total, t) => total + t.valor, 0);

    const pagamentosPagos = transacoesUnicas
      .filter(
        (t) =>
          t.tipo === 'entrada' &&
          t.status === 'pago' &&
          inPagPeriodo(t.data_pagamento)
      )
      .reduce((total, t) => total + t.valor, 0);

    const clientesPendentes = new Set(
      transacoesUnicas
        .filter(
          (t) =>
            t.tipo === 'entrada' &&
            t.status === 'pendente' &&
            inVencPeriodo(t.data_vencimento)
        )
        .map((t) => t.cliente_id)
    );

    const clientesEmAtraso = new Set(
      transacoesUnicas
        .filter((t) => {
          const dataVencimento = new Date(`${t.data_vencimento}T00:00:00`);
          return (
            t.tipo === 'entrada' &&
            t.status === 'pendente' &&
            dataVencimento < hoje
          );
        })
        .map((t) => t.cliente_id)
    );

    const pagamentosRealizados = transacoesUnicas.filter(
      (t) =>
        t.tipo === 'entrada' &&
        t.status === 'pago' &&
        inPagPeriodo(t.data_pagamento)
    ).length;

    const despesasPendentes = transacoesUnicas
      .filter((t) => t.tipo === 'saida' && t.status === 'pendente')
      .reduce((total, t) => total + t.valor, 0);

    const despesasPagas = transacoesUnicas
      .filter((t) => t.tipo === 'saida' && t.status === 'pago')
      .reduce((total, t) => total + t.valor, 0);

    const totalDespesasMes = transacoesUnicas
      .filter(
        (t) =>
          t.tipo === 'saida' &&
          t.status === 'pago' &&
          inPagPeriodo(t.data_pagamento)
      )
      .reduce((total, t) => total + t.valor, 0);

    return {
      receitaMensal,
      pagamentosPendentes,
      pagamentosEmAtraso,
      pagamentosPagos,
      totalClientesPendentes: clientesPendentes.size,
      totalClientesEmAtraso: clientesEmAtraso.size,
      totalPagamentosRealizados: pagamentosRealizados,
      percentualCrescimento,
      despesasPendentes,
      despesasPagas,
      totalDespesasMes,
      orcamentosAprovadosValor,
      temFiltroAtivo,
      periodoVencimentoLabel,
      periodoPagamentoLabel,
      mostrarCrescimento: !temFiltroCustomizado,
    };
  }, [
    transacoes,
    transacoesError,
    sinistros,
    orcamentosAprovados,
    filtros,
    periodosEfetivos,
  ]);

  return stats;
}
